import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ChevronDownIcon } from '@rsos/assets/icons';
import { Box, Checkbox } from '@rsos/base-components';
import {
  CustomAccordionWrapper,
  CustomAccordionHeader,
  CustomAccordionContent,
  CaretWrapper,
} from './LayerCheckbox.styles';
import {
  GISSupplementalLayer,
  Sublayer,
  GISService,
  RootState,
} from './LayerCheckbox.types';
import { LayerOpacityControl } from './LayerOpacityControl/LayerOpacityControl';

interface SupplementalLayerCheckboxProps {
  layer: GISSupplementalLayer;
  layerID: number | string;
  isDisabled: boolean;
  activeGISServices: GISService[];
  handleGISCheckboxOnChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  handleSublayerCheckboxOnChange: (
    sublayer: Sublayer,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const SupplementalLayerCheckbox: React.FC<SupplementalLayerCheckboxProps> = ({
  layer,
  layerID,
  isDisabled,
  activeGISServices,
  handleGISCheckboxOnChange,
  handleSublayerCheckboxOnChange,
}) => {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  const activeSupplementalServicesSublayers = useSelector(
    (state: RootState) => state.gisServices.activeSupplementalServicesSublayers,
  );

  const toggleAccordion = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsAccordionExpanded(!isAccordionExpanded);
    },
    [isAccordionExpanded],
  );

  const handleCheckboxClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const isMainLayerChecked = !!activeGISServices.find(
    (service: GISService) => service.id === layerID,
  );

  return (
    <CustomAccordionWrapper>
      <CustomAccordionHeader
        isExpanded={isAccordionExpanded}
        onClick={toggleAccordion}
      >
        <Box onClick={handleCheckboxClick} p={0}>
          <Checkbox
            label={layer.display_name}
            isChecked={isMainLayerChecked}
            disabled={isDisabled}
            onChange={handleGISCheckboxOnChange}
          />
        </Box>
        <CaretWrapper isExpanded={isAccordionExpanded}>
          <ChevronDownIcon color="secondaryText" width={10} height={10} />
        </CaretWrapper>
      </CustomAccordionHeader>

      {isAccordionExpanded && (
        <CustomAccordionContent>
          {layer.sublayers?.map((sublayer, index) => {
            return (
              <Box
                key={sublayer.sublayerID || sublayer.id || index}
                p={0}
                horizontal
              >
                <Box grow>
                  <Checkbox
                    label={
                      sublayer.sublayerName ||
                      sublayer.title ||
                      `Sublayer ${index + 1}`
                    }
                    isChecked={
                      !!activeSupplementalServicesSublayers.find(
                        activeSublayer =>
                          activeSublayer.sublayerID === sublayer.sublayerID,
                      )
                    }
                    disabled={isDisabled}
                    onChange={handleSublayerCheckboxOnChange(sublayer)}
                  />
                </Box>
                {sublayer.sublayerID && (
                  <LayerOpacityControl
                    sublayerId={String(sublayer.sublayerID)}
                    layerName={sublayer.sublayerName}
                  />
                )}
              </Box>
            );
          })}
        </CustomAccordionContent>
      )}
    </CustomAccordionWrapper>
  );
};

export default SupplementalLayerCheckbox;
